import { Link, useLocation } from 'react-router';

interface FlyoutItem {
  label: string;
  href: string;
  pageKey: string;
  activeTab?: string;
  isDefaultTab?: boolean;
}

interface SectionFlyoutProps {
  sectionLabel: string | null;
  items: FlyoutItem[];
  onItemClick: (pageKey: string) => void;
  onClose: () => void;
}

export const SectionFlyout = ({
  sectionLabel,
  items,
  onItemClick,
  onClose,
}: SectionFlyoutProps) => {
  const location = useLocation();
  const cleanPath = location.pathname.replace(/\/+$/, '');
  const currentPageKey = cleanPath.split('/').slice(-1)[0];

  if (!sectionLabel) return null;

  return (
    <div className="w-[180px] bg-white rounded-lg shadow-lg border border-grey-900 py-2">
      <div className="px-3 pb-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-t-200">
          {sectionLabel}
        </p>
      </div>
      {items.map((item) => {
        const hash = location.hash.replace('#', '');
        const pathMatches = currentPageKey === item.pageKey;
        // No hash means the page is showing its first tab (MCV-1464), so
        // mark the group's default entry active — mirrors NavEntry.
        const isActive = item.activeTab
          ? pathMatches && (hash ? hash === item.activeTab : Boolean(item.isDefaultTab))
          : pathMatches;

        return (
          <Link
            key={item.activeTab ? `${item.pageKey}-${item.activeTab}` : item.pageKey}
            to={{
              pathname: item.href,
              hash: item.activeTab ? `#${item.activeTab}` : undefined,
            }}
            onClick={() => {
              onItemClick(item.pageKey);
              onClose();
              window.scrollTo(0, 0);
            }}
            className="block mx-1.5"
            aria-current={isActive ? 'page' : undefined}
          >
            <div
              className={`px-2.5 py-1.5 rounded-md text-[12px] cursor-pointer transition-colors duration-150 ${
                isActive
                  ? 'bg-primary-t-900 text-primary-50 font-medium'
                  : 'text-grey-200 hover:bg-grey-900/50'
              }`}
            >
              {item.label}
            </div>
          </Link>
        );
      })}
    </div>
  );
};
